// Shared loading / error / empty states for data views, in the ArcLine visual
// language. Pairs with the useAsync hook: a view renders <Loading/> on first
// load, <ErrorState/> (with a retry) on failure, and <Empty/> when a successful
// load returns nothing — so a slow or failed request is never a blank panel.
import type { ReactNode } from "react";
import { c, font } from "../lib/theme";

function Center({ children, minHeight = 220 }: { children: ReactNode; minHeight?: number }) {
  return <div style={{ display: "grid", placeItems: "center", minHeight, textAlign: "center", padding: 16 }}>{children}</div>;
}

export function Loading({ label = "Loading", minHeight }: { label?: string; minHeight?: number }) {
  return (
    <Center minHeight={minHeight}>
      <div>
        <div style={{ width: 26, height: 26, margin: "0 auto 12px", borderRadius: "50%", border: `2px solid ${c.soft.track}`, borderTopColor: c.soft.accent2, animation: "arc-spin 0.7s linear infinite" }} />
        <div style={{ font: `500 14px ${font.sans}`, color: c.muted }}>{label}…</div>
      </div>
    </Center>
  );
}

// Maps a few raw fetch/auth errors to plain language; passes others through.
function friendly(message: string): string {
  if (message === "unauthenticated") return "Your session expired — sign in again to continue.";
  if (message === "Failed to fetch" || message === "Load failed") return "Can't reach the server. Check your connection and retry.";
  if (message === "forbidden") return "You don't have permission to view this.";
  return message;
}

export function ErrorState({ message, onRetry, minHeight }: { message: string; onRetry?: () => void; minHeight?: number }) {
  return (
    <Center minHeight={minHeight}>
      <div style={{ maxWidth: 400, background: c.soft.tile, border: "none", borderRadius: 10, padding: "22px 24px", boxShadow: "0 1px 2px rgba(0,0,0,0.28), 0 12px 32px -18px rgba(0,0,0,0.9)" }}>
        <div style={{ font: `700 19px ${font.display}`, color: c.soft.coral, marginBottom: 8 }}>Couldn’t load</div>
        <div style={{ fontSize: 14.5, color: c.muted2, lineHeight: 1.55, marginBottom: onRetry ? 16 : 0 }}>{friendly(message)}</div>
        {onRetry && (
          <button onClick={onRetry} style={{ font: `600 13.5px ${font.sans}`, color: c.onAccent, background: c.soft.accent2, border: "none", padding: "9px 18px", borderRadius: 6, cursor: "pointer" }}>Retry</button>
        )}
      </div>
    </Center>
  );
}

// A skeleton placeholder shaped like the content that's about to arrive
// (a grid of blank cells per row) — used instead of <Loading/> on the 4 views
// with the largest lists, so a navigation doesn't blank the whole screen.
const bone: React.CSSProperties = { height: 11, borderRadius: 4, background: c.line2, animation: "arc-pulse 1.4s ease-in-out infinite" };

export function SkeletonRows({ count = 10, columns = 4, gridTemplateColumns }: { count?: number; columns?: number; gridTemplateColumns?: string }) {
  return (
    <div aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: gridTemplateColumns ?? `repeat(${columns}, 1fr)`, gap: 12, padding: "11px 16px", borderTop: i > 0 ? `1px solid ${c.line}` : "none" }}>
          {Array.from({ length: columns }, (_, j) => (
            <div key={j} style={{ ...bone, width: j === 0 ? "70%" : "45%", animationDelay: `${(i * columns + j) * 0.02}s` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonCards({ count = 6 }: { count?: number }) {
  return (
    <div aria-hidden style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} style={{ background: c.soft.tile, border: "none", borderRadius: 10, padding: "20px 22px" }}>
          <div style={{ ...bone, width: "35%", marginBottom: 10, animationDelay: `${i * 0.03}s` }} />
          <div style={{ ...bone, width: "60%", animationDelay: `${i * 0.03 + 0.05}s` }} />
        </div>
      ))}
    </div>
  );
}

// Simple client-side Prev/Next pager — for the lists that fetch everything up
// front but shouldn't render more than a page's worth of DOM at once.
export function Pager({ page, pageCount, onPage }: { page: number; pageCount: number; onPage: (p: number) => void }) {
  if (pageCount <= 1) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "12px 0" }}>
      <button onClick={() => onPage(Math.max(0, page - 1))} disabled={page === 0}
        style={{ font: `600 13.5px ${font.sans}`, color: page === 0 ? c.muted : c.textHi, background: c.soft.tileHi, border: "none", borderRadius: 6, padding: "8px 14px", cursor: page === 0 ? "not-allowed" : "pointer" }}>← Prev</button>
      <span style={{ fontVariantNumeric: "tabular-nums", font: `500 13.5px ${font.sans}`, color: c.muted }}>Page {page + 1} of {pageCount}</span>
      <button onClick={() => onPage(Math.min(pageCount - 1, page + 1))} disabled={page === pageCount - 1}
        style={{ font: `600 13.5px ${font.sans}`, color: page === pageCount - 1 ? c.muted : c.textHi, background: c.soft.tileHi, border: "none", borderRadius: 6, padding: "8px 14px", cursor: page === pageCount - 1 ? "not-allowed" : "pointer" }}>Next →</button>
    </div>
  );
}

export function Empty({ title, note, icon = "◇", minHeight }: { title: string; note?: ReactNode; icon?: string; minHeight?: number }) {
  return (
    <Center minHeight={minHeight}>
      <div style={{ maxWidth: 360 }}>
        <div style={{ fontSize: 34, color: c.muted, marginBottom: 10, opacity: 0.55 }}>{icon}</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: c.muted2, marginBottom: 5 }}>{title}</div>
        {note && <div style={{ fontSize: 14, color: c.muted, lineHeight: 1.55 }}>{note}</div>}
      </div>
    </Center>
  );
}
